import {
  useCallback,
  useLayoutEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import * as headerCellStyles from '../components/DataTableHeaderCell.sty.js';
import { DATA_TABLE_NARROW_HEADER_THRESHOLDS } from '../constants.js';

interface UseHeaderCellInteractionsParams {
  headerCell: HTMLDivElement | null;
  canSort: boolean;
  canColumnActions: boolean;
  isActiveFloatingMenu: boolean;
  rowDensity: 'default' | 'condensed' | 'comfortable';
  toggleSortingHandler?: (event: unknown) => void;
}

interface UseHeaderCellInteractionsResult {
  handleCellActionsClose: () => void;
  handleCellMouseEnter: () => void;
  handleCellMouseLeave: (
    event: ReactMouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;
  handleCellClick: (event: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
}

/**
 * Checks whether an element is interactive.
 */
function isInteractiveElement(element: HTMLElement | SVGElement): boolean {
  const interactiveTags = ['BUTTON', 'A', 'LABEL', 'INPUT', 'SELECT'];

  return !!(
    interactiveTags.includes(element.tagName) ||
    element.getAttribute('tabindex')
  );
}

export function useHeaderCellInteractions({
  headerCell,
  canSort,
  canColumnActions,
  isActiveFloatingMenu,
  rowDensity,
  toggleSortingHandler,
}: UseHeaderCellInteractionsParams): UseHeaderCellInteractionsResult {
  const isHoveredRef = useRef(false);
  const isNarrowRef = useRef(false);

  function clearHoverState(cell: HTMLDivElement): void {
    isHoveredRef.current = false;
    isNarrowRef.current = false;
    // remove the hovered and narrow state
    cell.classList.remove(headerCellStyles.hovered, headerCellStyles.narrow);
  }

  // When the floating menu opens for this header, React's re-render overwrites
  // element.className and drops the imperatively-managed `hovered` class. Restore it
  // so the sorting arrow stays visible while the cursor is still over the column.
  useLayoutEffect(() => {
    if (!isActiveFloatingMenu || !isHoveredRef.current || !headerCell) {
      return;
    }
    headerCell.classList.add(headerCellStyles.hovered);
  }, [isActiveFloatingMenu, headerCell]);

  const handleCellActionsClose = useCallback((): void => {
    if (headerCell) {
      clearHoverState(headerCell);
    }
  }, [headerCell]);

  const handleCellMouseEnter = useCallback((): void => {
    if (!headerCell || (!canColumnActions && !canSort)) {
      return;
    }

    isHoveredRef.current = true;
    headerCell.classList.add(headerCellStyles.hovered);

    const widthThreshold = DATA_TABLE_NARROW_HEADER_THRESHOLDS[rowDensity];
    if (!widthThreshold) {
      return;
    }

    const headerWidth = headerCell.getBoundingClientRect().width;
    isNarrowRef.current = headerWidth <= widthThreshold;
    if (isNarrowRef.current) {
      headerCell.classList.add(headerCellStyles.narrow);
    }
  }, [canSort, canColumnActions, headerCell, rowDensity]);

  const handleCellMouseLeave = useCallback(
    (event: ReactMouseEvent<HTMLDivElement, MouseEvent>): void => {
      if (!headerCell) {
        return;
      }

      // When the column actions menu is opened on right-click, its trigger button is portalled
      // into the document.body which fires mouseleave on the header cell with relatedTarget pointing
      // at that button. Since the floating menu lives outside the header cell's subtree, we need
      // to detect it via relatedTarget and keep the hovered state.
      if (
        event.relatedTarget instanceof HTMLElement &&
        event.relatedTarget.dataset.trigger === 'context-action'
      ) {
        return;
      }

      const columnActionsMenu = headerCell.querySelector(
        '[data-trigger="useraction"]',
      );
      const isOpen = columnActionsMenu?.getAttribute('data-state') === 'open';
      if (isOpen) {
        // keep the hovered state while the column actions menu is open
        return;
      }

      // Ensure that inline cell actions menu remains visible during sorting by header click.
      // When sort state changes (unsorted ↔ sorted), the CSS marginRight offset changes,
      // causing a layout shift that fires mouseleave while the pointer is still inside the cell.
      if (headerCell.matches(':hover')) {
        return;
      }

      clearHoverState(headerCell);
    },
    [headerCell],
  );

  const handleCellClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement, MouseEvent>): void => {
      const { target } = event;
      if (!(target instanceof HTMLElement) && !(target instanceof SVGElement)) {
        return;
      }

      if ('headerRow' in target.dataset) {
        toggleSortingHandler?.(event);
        return;
      }

      const columnHeader = target.closest('[role="columnheader"]');
      if (!columnHeader) {
        return;
      }

      let currentElement: typeof target | null = target;
      while (currentElement && currentElement !== columnHeader) {
        if (isInteractiveElement(currentElement)) {
          return;
        }
        currentElement = currentElement.parentElement;
      }

      toggleSortingHandler?.(event);
    },
    [toggleSortingHandler],
  );

  return {
    handleCellActionsClose,
    handleCellMouseEnter,
    handleCellMouseLeave,
    handleCellClick,
  };
}
